import { PANORAMA_FINDING_DOCS } from './panoramaFindings.generated';
import type { HansAckerFinding, HansAckerReport } from './types';

const notAssessed = 'No findings — the repository was unavailable for review during this scan.';

// The run filed no vulnerabilities (the workspace was empty); its coverage
// gaps are surfaced as informational review items so nothing is hidden.
const findings: HansAckerFinding[] = Object.entries(PANORAMA_FINDING_DOCS).map(([id, doc]) => ({
  id,
  severity: 'info',
  type: 'review',
  folder: 'repo',
  location: doc.meta.find(([k]) => k === 'Surface')?.[1] ?? 'panorama360-backend',
  confidence: '\u2014',
  doc,
}));

export const panorama360Report: HansAckerReport = {
  projectLabel: 'panorama360-backend',
  verdict: { label: 'Overall security posture', headline: 'Not assessed — repository unavailable' },
  businessRisk:
    'Risk could not be assessed: the scan workspace contained no source code, so no code-driven analysis ran and no vulnerabilities were filed. The absence of findings is not evidence of a clean codebase.',
  scanDetails:
    'White-box repository assessment aligned with OWASP WSTG and internal secure code review practices. Scope: panorama360-backend at /workspace/panorama360-backend. Planned activities — repository mapping, authentication and authorization analysis, Semgrep and AST static analysis, secrets scanning, dependency and misconfiguration scanning, and local run preparation — were blocked because the repository directory was empty during the run. A baseline threat model was recorded to coordinate a future pass.',
  meta: { scanned: '14 Oct 2026, 07:16 UTC', duration: '6 min', mode: 'Quick', surfaces: 9, note: 'Assessment blocked — repository was empty during the scan' },
  severitySummary: {
    critical: notAssessed,
    high: notAssessed,
    medium: notAssessed,
    info: 'Nine review items need follow-up — the repository was unavailable, so no area could be assessed.',
  },
  fixFirst: [],
  findings,
  coverage: {
    surfacesReviewed: 9,
    riskAreas: 9,
    chips: [
      { label: '9 surfaces reviewed', tone: 'muted' },
      { label: '9 need follow-up', tone: 'warn' },
    ],
    items: [
      { status: 'na', name: 'Repository mapping / architecture', detail: 'Workspace path contained no visible files during this run; code mapping deferred' },
      { status: 'na', name: 'Route inventory / handlers', detail: 'No code available; routes and handlers could not be identified' },
      { status: 'na', name: 'Authentication / authorization controls', detail: 'No auth-related source files present; mapping postponed until code appears' },
      { status: 'na', name: 'Sinks (DB / FS / HTTP / XML)', detail: 'No source code available to inspect; sinks to be cataloged when code is accessible' },
      { status: 'na', name: 'Repository architecture and route mapping', detail: 'Workspace repository path empty; route inventory cannot be derived' },
      { status: 'na', name: 'Static SAST (Semgrep + AST-grep)', detail: 'Repository directory not found; tools could not run' },
      { status: 'na', name: 'Secrets scanning (gitleaks / trufflehog)', detail: 'Repository directory not found; scanners could not execute' },
      { status: 'na', name: 'Dependency & misconfiguration scanning (trivy fs)', detail: 'Repository directory not found; trivy fs not run' },
      { status: 'na', name: 'Run / build feasibility', detail: 'Repository directory exists but contains no files; build/run blocked' },
    ],
    followUp: [
      'Populate the panorama360-backend repository into the scan workspace and re-run the white-box assessment.',
      'Once code is present, run the static triage bundle (Semgrep, AST-grep, secrets, dependency scans) and map routes and authorization checks.',
    ],
  },
  recommendations: [
    {
      id: 'immediate',
      title: 'Immediate',
      items: [
        'Ensure the panorama360-backend repository is populated into the scan workspace, then re-run the white-box assessment.',
      ],
    },
    {
      id: 'short',
      title: 'Short-term',
      items: [
        'Orient on recent changes (git log, diffs) and run the static triage bundle: Semgrep, AST-grep, secrets and dependency scanning.',
        'Map routes/handlers and authorization checks; prioritize IDOR, injection, SSRF, authn/JWT and business-logic testing.',
        'Identify build/run steps and validate high-impact candidates dynamically where feasible.',
      ],
    },
    {
      id: 'medium',
      title: 'Medium-term',
      items: [
        'Enforce deny-by-default authorization middleware and consistent input validation, and establish a repeatable security CI pipeline.',
        'Re-test findings and update the threat model and coverage ledger once the repository is available.',
      ],
    },
  ],
  scans: [
    { id: 'panorama360-backend_6e97', scannedAt: '14 Oct 2026, 07:16 UTC', duration: '6 min', mode: 'Quick', findings: 9, latest: true, downloadUrl: '/hans-acker/scans/panorama360-backend_6e97.zip' },
  ],
  downloads: {
    zip: '/hans-acker/scans/panorama360-backend_6e97.zip',
  },
};
